import { Formik, Form } from 'formik'
import Column from '@components/column'
import SectionHeading from '@commons/headings/section'
import { Fragment, useEffect, useState } from 'react'
import {
  StyleHalfCardWrapper,
  StyleRow,
  StyleFormGrid,
  StyleWorkWrapper,
  StyleWorkItem,
  StyleAddCircle,
  StyleImageRow,
  StyleDiscountPercent,
  StyleDiscountRow,
} from '../../style'
import Alert from '@commons/popups/alert'
import SelectedLabTests from '@commons/categorySearch/selectedItems'
import SearchLabTests from '@commons/categorySearch/search'
import CardHeading from '@commons/headings/card'
import Fields from '@components/fields'
import Row from '@components/row'
import Button from '@components/button'
import Typography from '@components/typography'
import Svg from '@components/svg'
import ChooseCategories from '@commons/popups/categories'
import Image from '@components/image'
import { InstituteLabTest } from '@apis'
import { InstituteLabTestSchema } from '@validations'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Helper } from '@utils'
import { useSelector } from 'react-redux'
const InstitutionLabTestFormPage = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { slug } = useParams()
  const id = Helper.getIdFromSlug(slug)
  const { data } = useSelector((state: any) => state.instituteLabTest)
  const labTest = !!state?.id ? state : data
  const [initialValues, setInitialValues] = useState({
    name: '',
    description: '',
    categories: [],
    images: ['', '', '', ''],
    symptoms: [],
    infos: {
      expectedResultTime: '',
      suggestedGender: '',
      availability: '',
      suggestedAgeGroups: '',
      price: '',
      additionalNote: '',
      works: [''],
      status: true,
      discount: '',
      discountType: 'Percentage',
    },
  })
  const [loading, setLoading] = useState<boolean>(false)
  const [visible, setVisible] = useState<boolean>(false)
  const [deleteInfo, setDeleteInfo] = useState({
    visible: false,
    loading: false,
  })

  const onClickDelete = () => {
    setDeleteInfo({
      visible: true,
      loading: false,
    })
  }
  const removeData = async () => {
    setDeleteInfo({
      ...deleteInfo,
      loading: true,
    })
    await InstituteLabTest.remove({
      id,
      state: 'instituteLabTests',
    })
      .then(() => {
        navigate(`/institution/lab-tests`)
      })
      .catch(() => {})
    setDeleteInfo({
      ...deleteInfo,
      visible: false,
      loading: false,
    })
  }
  const fetchData = async () => {
    await InstituteLabTest.find({
      state: 'instituteLabTest',
      id: id,
    }).catch(() => {})
  }

  const submitHandler = async (values) => {
    setLoading(true)
    const payload: any = {
      name: values?.name,
      description: values?.description,
      categories: values?.categories?.map((labTest) => labTest?.id),
      symptoms: values?.symptoms?.map((symptom: any) => symptom?.id),
      infos: {
        ...values?.infos,
        isPackage: false,
        price: parseFloat(values?.infos?.price),
        discount: parseFloat(values?.infos?.discount || 0),
      },
    }
    if (!!values?.images?.length) {
      const images = values?.images
        ?.filter((image: any) => !!image && !!image?.base64)
        ?.map((image) => {
          const item: any = {
            base64: image?.base64,
            extension: image?.extension,
          }
          if (image?.id) {
            item.id = image.id
          }
          return item
        })
      if (!!images?.length) {
        payload.images = images
      }
    }
    if (!!labTest?.id) {
      await InstituteLabTest.update({
        data: payload,
        id,
        state: 'instituteLabTests',
      })
        .then(() => {
          navigate('/institution/lab-tests')
        })
        .catch(() => {})
    } else {
      await InstituteLabTest.create({
        data: payload,
        state: 'instituteLabTests',
      })
        .then(() => {
          navigate('/institution/lab-tests')
        })
        .catch(() => {})
    }
    setLoading(false)
  }

  useEffect(() => {
    if (!state?.id && !!id) {
      fetchData()
    }
    if (!!labTest?.id) {
      setInitialValues({
        ...initialValues,
        name: labTest?.name,
        description: labTest?.description,
        categories: labTest?.categories,
        images: Array(4)
          .fill('')
          ?.map((_, index) => {
            return labTest?.images[index]?.image || ''
          }),
        symptoms: labTest?.symptoms,
        infos: labTest?.infos,
      })
    }
  }, [id, labTest?.id])

  return (
    <Column rowGap={20}>
      <SectionHeading
        title={`${!!id ? 'Update' : 'Create'} a Single Lab Test`}
        tc="primary"
      />
      <Formik
        onSubmit={submitHandler}
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={InstituteLabTestSchema.singleValidationSchema}
      >
        {({ handleSubmit, values, setFieldValue, errors }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <Column rowGap={20}>
                {!!labTest?.id && (
                  <Row
                    bg="white"
                    styleRow="border-radius:12px;padding:15px 20px;"
                    rowGap={20}
                    alignItems="center"
                  >
                    <Fields.Switch
                      name="infos.status"
                      label="Lab Test Status"
                      styleWrapper="display:flex;flex-direction:row;column-gap:10px;"
                    />
                    <Button
                      title="Delete"
                      bg="danger"
                      outline
                      onClick={onClickDelete}
                    />
                  </Row>
                )}
                <StyleRow>
                  <StyleHalfCardWrapper>
                    <SelectedLabTests
                      selectedItems={
                        !!values?.categories ? values?.categories : []
                      }
                      setFieldValue={setFieldValue}
                      name="categories"
                      error={errors.categories}
                    />
                  </StyleHalfCardWrapper>
                  <StyleHalfCardWrapper>
                    <SearchLabTests
                      setFieldValue={setFieldValue}
                      selectedItems={
                        !!values?.categories?.length ? values?.categories : []
                      }
                      name="categories"
                      selector="labTestCategories"
                      type="Lab Test"
                    />
                  </StyleHalfCardWrapper>
                </StyleRow>
                <Column
                  bg="white"
                  styleColumn="border-radius:12px;padding:15px 20px;"
                  rowGap={20}
                >
                  <CardHeading title="Lab Test Details" />
                  <StyleFormGrid>
                    <Fields.Input
                      name="name"
                      label="Name"
                      placeholder="Enter Name"
                    />
                    <Fields.Input
                      name="infos.expectedResultTime"
                      label="Expected Time"
                      placeholder="Enter Expected Time"
                    />
                    <Fields.DropDown
                      label="Suggested Gender"
                      name="infos.suggestedGender"
                      options={[
                        {
                          label: 'Male',
                        },
                        {
                          label: 'Female',
                        },
                        {
                          label: 'Both',
                        },
                      ]}
                      titleAsKey="label"
                      valueAsKey="label"
                      placeholder="Select Suggested Gender"
                    />
                    <Fields.DropDown
                      label="Availability"
                      name="infos.availability"
                      placeholder="Select Availability"
                      options={[
                        {
                          label: 'Home',
                        },
                        {
                          label: 'Clinic',
                        },
                        {
                          label: 'Both',
                        },
                      ]}
                      titleAsKey="label"
                      valueAsKey="label"
                    />
                  </StyleFormGrid>

                  <Fields.GroupCheckBox
                    label="Suggested Age Group"
                    name="infos.suggestedAgeGroups"
                    titleAsKey="label"
                    valueAsKey="label"
                    options={[
                      {
                        label: 'Under 18 Years',
                      },
                      {
                        label: '19-25 Years',
                      },
                      {
                        label: '26-35 Years',
                      },
                      {
                        label: '36-50 Years',
                      },
                      {
                        label: 'Above 50 Years',
                      },
                    ]}
                    styleItem="
                            border:1px solid #E9E9E9;
                            padding:5px 10px;
                            border-radius:4px;
                        "
                  />

                  <StyleFormGrid>
                    <Fields.Input
                      type="number"
                      name="infos.price"
                      label="Lab Test Price"
                      placeholder="Lab Test Price"
                    />
                    <StyleDiscountRow>
                      <Fields.Input
                        type="number"
                        name="infos.discount"
                        label="Apply Product Dicount"
                        placeholder="Apply Product Discount"
                        styleInput="  border-top-right-radius: 0px;
                        border-bottom-right-radius: 0px;"
                      />
                      <StyleDiscountPercent
                        onClick={() => {
                          setFieldValue(
                            'infos.discountType',
                            values?.infos?.discountType == 'Percentage'
                              ? 'Flat'
                              : 'Percentage',
                          )
                        }}
                      >
                        {values?.infos?.discountType == 'Percentage'
                          ? '%'
                          : 'Rs.'}{' '}
                        <Svg svg="recycle" size={30} />
                      </StyleDiscountPercent>
                    </StyleDiscountRow>
                  </StyleFormGrid>
                  <Fields.TextArea
                    name="infos.additionalNote"
                    label="Additional Note"
                    placeholder="Enter additional note/ eg. 10-12 fasting required for this Test"
                    rows={3}
                  />
                  <Column rowGap={15}>
                    <Typography
                      content="Lab Test For:"
                      size={14}
                      weight={500}
                    />
                    <ChooseCategories
                      selectedItems={values?.symptoms}
                      visible={visible}
                      setVisible={setVisible}
                      selector="symptoms"
                      type="Symptom"
                      title="Symptom"
                      onClick={(item: any) => {
                        const findIndex = values?.symptoms?.findIndex(
                          (selected: any) => selected?.id == item.id,
                        )
                        if (findIndex >= 0) {
                          setFieldValue(
                            'symptoms',
                            values?.symptoms?.filter(
                              (symptom) => symptom?.id != item?.id,
                            ),
                          )
                        } else {
                          setFieldValue('symptoms', [
                            ...(values?.symptoms || []),
                            item,
                          ])
                        }
                      }}
                    />
                    <Row styleRow="flex-flow:row wrap;" rowGap={10} colGap={15}>
                      <Column alignItems="center" rowGap={5}>
                        <StyleAddCircle onClick={() => setVisible(true)}>
                          <Svg svg="plus" />
                        </StyleAddCircle>
                        <Typography content="Add" size={12} />
                      </Column>
                      {values?.symptoms?.map((symptom: any) => {
                        return (
                          <Fragment key={symptom?.id}>
                            <Column rowGap={5}>
                              <Image
                                width={50}
                                height={50}
                                styleImage={`border-radius:50%;`}
                                src={`${process.env.BUCKET_URL}/${symptom?.infos?.image}`}
                              />
                              <Typography
                                content={symptom?.name}
                                size={12}
                                styleText="text-align:center"
                              />
                            </Column>
                          </Fragment>
                        )
                      })}
                    </Row>
                  </Column>
                  <StyleImageRow>
                    {values?.images?.map((_, index: any) => {
                      return (
                        <Fields.Upload
                          name={`images[${index}]`}
                          varient="drag"
                          label="Lab Test Image"
                          placeholder="Upload Image"
                          styleWrapper="width:275px;"
                          size={150}
                          key={index}
                          onChange={(file) => {
                            const item: any = {
                              base64: file?.base64,
                              extension: file?.extension,
                              info: file?.info,
                            }
                            if (!!id) {
                              if (labTest?.images?.[index]) {
                                item.id = labTest?.images[index]?.id
                              }
                            }
                            setFieldValue(`images[${index}]`, item)
                          }}
                        />
                      )
                    })}
                  </StyleImageRow>
                </Column>
                <Column
                  bg="white"
                  styleColumn="border-radius:12px;padding:15px 20px;"
                  rowGap={20}
                >
                  <Fields.TextArea
                    name="description"
                    label="Test Description"
                    placeholder="Enter Service Description"
                    rows={3}
                  />
                </Column>
                <Column
                  bg="white"
                  styleColumn="border-radius:12px;padding:15px 20px;"
                  rowGap={20}
                >
                  <Typography
                    content="How Our Test Works"
                    size={14}
                    tc="primary"
                    weight={500}
                  />
                  <StyleWorkWrapper>
                    {values?.infos?.works?.map((_, index) => {
                      return (
                        <Fragment key={index}>
                          <StyleWorkItem>
                            <Fields.Input
                              name={`infos.works[${index}]`}
                              placeholder="Enter Work"
                              styleInput="
                                border:0px;
                              "
                              onChange={(text) => {
                                setFieldValue(`infos.works[${index}]`, text)
                                if (index != 0) {
                                  if (!text) {
                                    setFieldValue(
                                      'infos.works',
                                      values?.infos?.works?.filter(
                                        (_, i) => i != index,
                                      ),
                                    )
                                  }
                                }
                              }}
                              onKeyDown={(e: any) => {
                                if (e.key == 'Enter') {
                                  e.preventDefault()
                                  setFieldValue('infos.works', [
                                    ...values?.infos?.works,
                                    '',
                                  ])
                                }
                              }}
                            />
                          </StyleWorkItem>
                        </Fragment>
                      )
                    })}
                  </StyleWorkWrapper>
                </Column>
                <Row justifyContent="flex-end">
                  <Button
                    title={!!id ? 'Update Lab Test' : 'Add Lab Test'}
                    height={45}
                    loading={loading}
                    type={'submit'}
                  />
                </Row>
              </Column>
            </Form>
          )
        }}
      </Formik>
      <Alert
        visible={deleteInfo?.visible}
        loading={deleteInfo?.loading}
        onClickOk={removeData}
        onClickClose={() => {
          setDeleteInfo({
            visible: false,
            loading: false,
          })
        }}
      />
    </Column>
  )
}

export default InstitutionLabTestFormPage
